import { RouteMeta, getCanonicalUrl } from './routeMeta';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const upsertMetaTag = (selector: string, attributes: Record<string, string>) => {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element!.setAttribute(key, value);
  });
};

const upsertLinkTag = (selector: string, attributes: Record<string, string>) => {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element!.setAttribute(key, value);
  });
};

export const renderMetaTags = (meta: RouteMeta): string => {
  const canonicalUrl = getCanonicalUrl(meta.canonicalPath);
  const title = escapeHtml(meta.title);
  const description = escapeHtml(meta.description);
  const ogImage = escapeHtml(meta.ogImage || '');
  const ogType = escapeHtml(meta.ogType || 'website');
  const canonical = escapeHtml(canonicalUrl);
  const robots = escapeHtml(meta.robots || 'index,follow');

  return [
    `<title>${title}</title>`,
    `<meta name="description" content="${description}" />`,
    `<meta name="robots" content="${robots}" />`,
    `<link rel="canonical" href="${canonical}" />`,
    `<meta property="og:type" content="${ogType}" />`,
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${description}" />`,
    `<meta property="og:url" content="${canonical}" />`,
    `<meta property="og:image" content="${ogImage}" />`,
    '<meta property="og:image:width" content="1200" />',
    '<meta property="og:image:height" content="630" />',
    '<meta property="twitter:card" content="summary_large_image" />',
    `<meta property="twitter:title" content="${title}" />`,
    `<meta property="twitter:description" content="${description}" />`,
    `<meta property="twitter:image" content="${ogImage}" />`,
  ].join('\n    ');
};

export const applyRouteMeta = (meta: RouteMeta) => {
  if (typeof document === 'undefined') {
    return;
  }

  const canonicalUrl = getCanonicalUrl(meta.canonicalPath);
  document.title = meta.title;

  upsertMetaTag('meta[name="description"]', {
    name: 'description',
    content: meta.description,
  });

  upsertMetaTag('meta[name="robots"]', {
    name: 'robots',
    content: meta.robots || 'index,follow',
  });

  upsertLinkTag('link[rel="canonical"]', {
    rel: 'canonical',
    href: canonicalUrl,
  });

  upsertMetaTag('meta[property="og:type"]', {
    property: 'og:type',
    content: meta.ogType || 'website',
  });

  upsertMetaTag('meta[property="og:title"]', {
    property: 'og:title',
    content: meta.title,
  });

  upsertMetaTag('meta[property="og:description"]', {
    property: 'og:description',
    content: meta.description,
  });

  upsertMetaTag('meta[property="og:url"]', {
    property: 'og:url',
    content: canonicalUrl,
  });

  upsertMetaTag('meta[property="og:image"]', {
    property: 'og:image',
    content: meta.ogImage || '',
  });

  upsertMetaTag('meta[property="og:image:width"]', {
    property: 'og:image:width',
    content: '1200',
  });

  upsertMetaTag('meta[property="og:image:height"]', {
    property: 'og:image:height',
    content: '630',
  });

  upsertMetaTag('meta[property="twitter:card"]', {
    property: 'twitter:card',
    content: 'summary_large_image',
  });

  upsertMetaTag('meta[property="twitter:title"]', {
    property: 'twitter:title',
    content: meta.title,
  });

  upsertMetaTag('meta[property="twitter:description"]', {
    property: 'twitter:description',
    content: meta.description,
  });

  upsertMetaTag('meta[property="twitter:image"]', {
    property: 'twitter:image',
    content: meta.ogImage || '',
  });
};
